import type { EquityPosition } from '@mfos/shared';
import { Link } from 'react-router';
import { formatDate, formatDateTime, formatPercent, formatShareCount } from '../../lib/format';
import { useMembership } from '../workspace/membership-context';
import { phrase } from '../../lib/i18n/runtime';

/** Approval provenance: the approving decision when present, otherwise the recorded reference. */
export function ApprovalSource({
  approvalDecisionId,
  approvalReference,
}: {
  approvalDecisionId: string | null;
  approvalReference: string | null;
}) {
  const membership = useMembership();
  if (approvalDecisionId !== null) {
    return (
      <>
        <Link to={`/w/${membership.workspace.id}/decisions/${approvalDecisionId}`}>
          {phrase('Approving decision')}
        </Link>{' '}
        <code className="id">{approvalDecisionId}</code>
      </>
    );
  }
  if (approvalReference !== null) {
    return <>Reference: {approvalReference}</>;
  }
  return <>{phrase('Not recorded')}</>;
}

/** An equity value is always shown with its effective date and provenance. */
export function EquityPositionDetails({
  position,
  timeZone,
}: {
  position: EquityPosition;
  timeZone: string | undefined;
}) {
  return (
    <dl className="summary-list">
      <div>
        <dt>{phrase('Ownership')}</dt>
        <dd className="figure">{formatPercent(position.percentage)}</dd>
      </div>
      <div>
        <dt>{phrase('Shares')}</dt>
        <dd>
          {position.shareCount !== null ? formatShareCount(position.shareCount) : phrase('Not set')}
        </dd>
      </div>
      <div>
        <dt>{phrase('Effective from')}</dt>
        <dd>
          <time dateTime={position.effectiveFrom}>{formatDate(position.effectiveFrom)}</time>
        </dd>
      </div>
      <div>
        <dt>{phrase('Source')}</dt>
        <dd>Cap table version #{position.versionNumber}</dd>
      </div>
      <div>
        <dt>{phrase('Approved by')}</dt>
        <dd>
          <ApprovalSource
            approvalDecisionId={position.approvalDecisionId}
            approvalReference={position.approvalReference}
          />
        </dd>
      </div>
      <div>
        <dt>{phrase('Approved at')}</dt>
        <dd>
          {position.approvedAt !== null ? (
            <time dateTime={position.approvedAt}>
              {formatDateTime(position.approvedAt, timeZone)}
            </time>
          ) : (
            'Not recorded'
          )}
        </dd>
      </div>
    </dl>
  );
}
